/* eslint-disable promise/always-return */
/* eslint-disable promise/prefer-await-to-then */
import { API } from '../../api';
import { Button, Form, Input, Modal, PageHeader, notification } from 'antd';
import { DimaContext } from '../../DimaContext';
import { Route, useRouteMatch } from 'react-router-dom';
import { USER_ROLE } from '../../consts';
import { checkPermission, isAdmin, sendErrorNotification } from '../../utils/util';
import ActionDetail from './actionDetail';
import ActionType from './actionType';
import IngestionDashboardTabs from './dashboardTabs';
import JobDetail from './jobDetail';
import JobType from './jobType';
import React, { useContext, useEffect, useState } from 'react';
import ScheduleDetail from './scheduleDetail';

/**
 * Dashboard showing information on data ingestion via communication
 * with the data ingest service.
 * Views for:
 * 1. Scheduling ingestion jobs
 * 2. Showing uploaded files
 * 3. Showing ingestion progress
 */

/**
 * NOTE: The DIS API URL updates have bugs (mainly that context does not get updated, so editing these twice leads to bugs with the placeholders)
 * but with the centralization of DIS, these fields will go away entirely. So not addressing this.
 */
const IngestionDashboard = () => {
  const dimaContext = useContext(DimaContext);
  const { client } = dimaContext;
  const [clientDisApi, setClientDisApi] = useState(client.data_ingest_api_url);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showEditAPIModal, setShowEditAPIModal] = useState(false);
  const [showTenancyModal, setShowTenancyModal] = useState(false);
  const [editedApiUrl, setEditedApiUrl] = useState('');
  const [isMultiTenant, setIsMultiTenant] = useState(client.use_multitenant_config);
  const [hasMultiTenantDIS, setHasMultiTenantDIS] = useState(false);
  const { path } = useRouteMatch();

  const getCurrentTenancyMode = () => {
    return isMultiTenant === true ? 'multi-tenant' : 'single-tenant';
  };

  const getOtherTenancyMode = () => {
    return isMultiTenant === true ? 'single-tenant' : 'multi-tenant';
  };

  const onEditCredentials = () => {
    setLoading(true);
    const body = {};

    if (editedApiUrl && editedApiUrl.trim().length > 0) {
      body.data_ingest_api_url = editedApiUrl;
    }

    if (Object.keys(body).length > 0) {
      API.put(`/client/${client.id}`, body)
        .then(() => {
          notification.success({
            message: 'Success!',
            description: `${client.name}'s DIS API URL has been updated successfully`,
            duration: 3,
            placement: 'topLeft',
          });
          setClientDisApi(editedApiUrl);
          setEditedApiUrl('');
        })
        .catch(e => {
          notification.error({
            message: 'ERROR: ',
            description: `DIS API URL failed to update with the following error: ${e}`,
            duration: 6,
            placement: 'topLeft',
          });
          setEditedApiUrl('');
        });
    } else {
      notification.error({
        message: 'ERROR: ',
        description: 'DIS API URL cannot be blank',
        duration: 6,
        placement: 'topLeft',
      });
      setEditedApiUrl('');
    }

    setLoading(false);
    setShowEditAPIModal(false);
  };

  /**
   * Return whether or not the client is in DIS. If the check couldn't be performed,
   * just return null.
   */
  const checkClientInDIS = async () => {
    try {
      const res = await API.get(`/dis/tenants?clientId=${client.id}`);
      /* eslint-disable-next-line prefer-destructuring, require-atomic-updates */
      client.tenant = res.data.results[0];
      return res.data.results.length > 0;
    } catch {
      notification.error({
        message: 'Error',
        description: 'Unable to lookup Tenants in DIS',
        duration: 3,
        placement: 'topLeft',
      });
      return false;
    }
  };

  const createClientInDIS = async () => {
    const newTenant = {
      name: client.name,
      dimaId: client.id,
      accountId: client.aws_account_id,
    };

    try {
      const res = await API.post(`/dis/tenants?clientId=${client.id}`, newTenant);
      notification.success({
        message: 'Tenant created',
        description: `A tenant was created for ${client.name}`,
        duration: 3,
        placement: 'topLeft',
      });
      /* eslint-disable-next-line prefer-destructuring */
      client.tenant = res.data[0];
      return true;
    } catch {
      notification.error({
        message: 'Error',
        description: 'Unable to create Tenant in DIS',
        duration: 3,
        placement: 'topLeft',
      });
      return false;
    }
  };

  const onSwitchTenantMode = async () => {
    setLoading(true);

    const body = { use_multitenant_config: !client.use_multitenant_config };
    // TODO: Note need to set actionType.subscriber to null in DIS when switching
    // Otherwise, DIS will create actions with the STE subscriber in MTE mode

    // First check if there is a MTE DIS and if client has a corresponding DIS tenant
    const isDISTenant = await checkClientInDIS();

    let shouldUpdate = isDISTenant;

    if (isDISTenant === false) {
      if (!client.aws_account_id) {
        setLoading(false);
        setShowTenancyModal(false);
        return notification.error({
          message: 'Error',
          description:
            'Unable to create Tenant in DIS. Please specify an AWS Account ID for the Client.',
          duration: 3,
          placement: 'topLeft',
        });
      }

      const postClientSuccess = await createClientInDIS();
      shouldUpdate = postClientSuccess;
    }

    if (shouldUpdate) {
      try {
        const resp = await API.put(`/client/${client.id}`, body);
        notification.success({
          message: 'Success!',
          description: `${client.name}'s tenancy mode has been updated successfully`,
          duration: 3,
          placement: 'topLeft',
        });
        // update client object to reflect
        client.use_multitenant_config = resp.data.use_multitenant_config;
        setIsMultiTenant(resp.data.use_multitenant_config);
        localStorage.setItem('selectedClient', JSON.stringify(client));
      } catch (e) {
        notification.error({
          message: 'ERROR: ',
          description: `Client tenancy mode failed to update with the following error: ${e}`,
          duration: 6,
          placement: 'topLeft',
        });
      }
    }

    setLoading(false);
    return setShowTenancyModal(false);
  };

  /**
   * Sends error notification
   * @param {errorInfo} errorInfo
   */
  const onFinishFailed = errorInfo => {
    sendErrorNotification(errorInfo);
    setEditedApiUrl('');
  };

  /**
   * Updates Client API Key when input field changes
   * @param {target} param
   */
  const handleApiUrlChange = ({ target }) => {
    const { value } = target;
    setEditedApiUrl(value);
  };

  useEffect(() => {
    const fetchTenantData = async () => {
      setLoading(true);
      const clientInDIS = await checkClientInDIS();
      if (clientInDIS === false) createClientInDIS();
      setLoading(false);
    };

    if (client && client.use_multitenant_config) fetchTenantData();
  }, [client]);

  useEffect(() => {
    const fetchHasMultiTenantDIS = async () => {
      const hasMultiTenantModeRes = await API.get('/dis/mte');
      setHasMultiTenantDIS(hasMultiTenantModeRes.data.hasMultiTenantDIS);
    };

    fetchHasMultiTenantDIS();
  }, []);

  return (
    <>
      {/* TODO: REMOVE THIS MODAL ONCE DIS IS CENTRALIZED */}
      <Modal
        title="Edit Client's DIS API URL"
        open={showEditAPIModal}
        onOk={onEditCredentials}
        onCancel={e => [e.stopPropagation(), setShowEditAPIModal(false)]}
        footer={null}
      >
        <Form
          form={form}
          name="basic"
          layout="horizontal"
          onFinish={onEditCredentials}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item name="client_dis_api_url" initialValue={clientDisApi}>
            <Input
              className="dis-input-width"
              data-cy="dis-api-url"
              addonBefore="Client API URL: "
              onChange={handleApiUrlChange}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              data-cy="dis-submit-creds-btn"
              loading={loading}
              style={{ float: 'left' }}
            >
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* TODO: REMOVE THIS MODAL ONCE DIS IS CENTRALIZED */}
      <Modal
        title="Switch tenancy mode"
        open={showTenancyModal}
        onOk={onSwitchTenantMode}
        onCancel={e => [e.stopPropagation(), setShowTenancyModal(false)]}
        footer={null}
      >
        {client.name} is currently on {getCurrentTenancyMode()}. Are you sure you want to switch to{' '}
        {getOtherTenancyMode()}?
        <Form
          form={form}
          name="basic"
          layout="horizontal"
          onFinish={onSwitchTenantMode}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              data-cy="dis-submit-tenancy-btn"
              loading={loading}
              style={{ float: 'left', marginTop: '20px' }}
            >
              Confirm
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <div style={{ display: 'flex', flexDirection: 'row', backgroundColor: '#f5f5f5' }}>
        <PageHeader className="site-page-header" title={`Ingestion Dashboard: ${client.name}`} />
        {
          // TODO: remove buttons once DIS is centralized
          // this is just meant for backwards-compatability while we're migrating to centralized, multitenant DIS
          (process.env.REACT_APP_FEATURE_PERMISSIONS_V2
            ? checkPermission(dimaContext.userData.permissions, 'editClient')
            : isAdmin(USER_ROLE)) && (
            <div
              style={{ marginLeft: 'auto', marginTop: '20px', marginRight: '20px', color: 'black' }}
            >
              <div style={{ display: 'flex' }}>
                <div>
                  Tenancy mode: <b>{getCurrentTenancyMode()}</b>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {hasMultiTenantDIS && (
                    <Button
                      style={{ marginLeft: '10px', marginTop: '-5px' }}
                      type="primary"
                      htmlType="button"
                      data-cy="dis-switch-tenant-mode-btn"
                      onClick={e => [e.stopPropagation(), setShowTenancyModal(true)]}
                    >
                      Switch Tenant Mode
                    </Button>
                  )}
                  {(process.env.REACT_APP_FEATURE_PERMISSIONS_V2
                    ? checkPermission(dimaContext.userData.permissions, 'editClient')
                    : isAdmin(USER_ROLE)) &&
                    getCurrentTenancyMode() === 'single-tenant' && (
                      <Button
                        style={
                          hasMultiTenantDIS
                            ? { margin: '10px' }
                            : { marginTop: '-5px', marginLeft: '10px' }
                        }
                        type="primary"
                        htmlType="button"
                        data-cy="dis-edit-creds-btn"
                        onClick={e => [e.stopPropagation(), setShowEditAPIModal(true)]}
                      >
                        Edit DIS API Url
                      </Button>
                    )}
                </div>
              </div>
            </div>
          )
        }
      </div>

      <Route exact path={path}>
        <IngestionDashboardTabs
          // clientDisAPI is used by Files tab. This should be refactored, but DIS will be centralized so it'll all change
          client={client}
          clientDisApi={clientDisApi}
        />
      </Route>
      <Route path={`${path}/schedule/:schedule_id`}>
        <ScheduleDetail client={client} />
      </Route>
      <Route path={`${path}/job/:jobId/action/:actionId`}>
        <ActionDetail client={client} />
      </Route>
      <Route exact path={`${path}/job/:jobId`}>
        <JobDetail client={client} />
      </Route>
      <Route path={`${path}/jobType/:name`}>
        <JobType client={client} />
      </Route>
      <Route path={`${path}/actionType/:name`}>
        <ActionType client={client} />
      </Route>
    </>
  );
};

export default IngestionDashboard;
